import React, { useRef, useEffect, useState } from "react";
import { ElementData, TrackingData, CatalystType, GameState } from "../types";
import { ELEMENTS, HOLOLAB_COLORS } from "../constants";
import explosionMeme from "../assets/image.png";

interface UIOverlayProps {
  leftElement: ElementData;
  rightElement: ElementData;
  combinedElement: ElementData | null;
  message: string;
  trackingRef: React.MutableRefObject<TrackingData>;
  activeCatalyst: CatalystType;
  labSlots: ElementData[]; // Dashboard slots (8 manually selected)
  labCreatedSlots: ElementData[]; // Lab-created slots (8 auto-discovered)
  isDashboardOpen: boolean;
  onToggleDashboard: () => void;
  savedElements: ElementData[];
  gameState?: GameState;
  deathReason?: string;
  showSixtySeven?: boolean;
  unlockedElement?: ElementData | null;
}

// Icons
const FlameIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-10 h-10"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M12 22c4.97 0 9-4.03 9-9 0-4.97-9-13-9-13S3 8.03 3 13c0 4.97 4.03 9 9 9z"
      fill="currentColor"
      fillOpacity="0.2"
    />
    <path
      d="M12 22c4.97 0 9-4.03 9-9 0-4.97-9-13-9-13S3 8.03 3 13c0 4.97 4.03 9 9 9z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 18c2.21 0 4-1.79 4-4 0-2.21-4-6-4-6s-4 3.79-4 6c0 2.21 1.79 4 4 4z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BoltIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-10 h-10"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
      fill="currentColor"
      fillOpacity="0.2"
    />
    <path
      d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FlaskIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-10 h-10"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M8.5 2h7M12 2v6M6 22h12a2 2 0 002-2l-3-9a6 6 0 00-6-3h-1a6 6 0 00-6 3l-3 9a2 2 0 002 2z"
      fill="currentColor"
      fillOpacity="0.2"
    />
    <path
      d="M8.5 2h7M12 2v6M6 22h12a2 2 0 002-2l-3-9a6 6 0 00-6-3h-1a6 6 0 00-6 3l-3 9a2 2 0 002 2z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="16" r="1" fill="currentColor" />
    <circle cx="14" cy="18" r="1" fill="currentColor" />
  </svg>
);

const PressureIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-10 h-10"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M12 8v8M8 12h8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 4l7 8-7 8M19 4l-7 8 7 8"
      fill="currentColor"
      fillOpacity="0.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 4l7 8-7 8M19 4l-7 8 7 8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DashboardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="w-8 h-8"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </svg>
);

const DeathScreen: React.FC<{ reason: string }> = ({ reason }) => {
    useEffect(() => {
        const t = setTimeout(() => {
            window.location.href = '/';
        }, 6000);
        return () => clearTimeout(t);
    }, []);

    return (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center animate-[fadeInDelayed_2s_ease-out_forwards]" style={{ backgroundColor: HOLOLAB_COLORS.deepBlue }}>
             <div className="absolute inset-0 z-0 flex items-center justify-center opacity-0 animate-[fadeInDelayed_3s_ease-out_forwards]">
                <img
                    src={explosionMeme}
                    alt="Explosion Meme"
                    className="max-w-full max-h-full object-contain opacity-40"
                />
            </div>
            <h1 className="relative z-10 text-6xl md:text-9xl font-['Inter'] font-bold tracking-widest uppercase scale-110 mb-8" style={{ color: HOLOLAB_COLORS.errorRed }}>
                YOU DIED
            </h1>
            <div className="relative z-10 max-w-2xl text-center px-4">
                <p className="text-xl md:text-2xl font-['JetBrains_Mono'] py-4" style={{ color: HOLOLAB_COLORS.labWhite, opacity: 0.7, borderTop: `1px solid ${HOLOLAB_COLORS.precisionBlue}40`, borderBottom: `1px solid ${HOLOLAB_COLORS.precisionBlue}40` }}>
                    {reason}
                </p>
            </div>
            <div className="relative z-10 mt-12 text-sm font-['Inter'] animate-pulse" style={{ color: HOLOLAB_COLORS.precisionBlue }}>
                RETURNING TO LAB...
            </div>
            <style>{`
                @keyframes fadeInDelayed {
                    0% { opacity: 0; }
                    30% { opacity: 0; }
                    100% { opacity: 1; }
                }
            `}</style>
        </div>
    );
};

const UIOverlay: React.FC<UIOverlayProps> = ({
  leftElement,
  rightElement,
  combinedElement,
  message,
  trackingRef,
  activeCatalyst,
  labSlots,
  labCreatedSlots,
  isDashboardOpen,
  onToggleDashboard,
  savedElements,
  gameState,
  deathReason,
  showSixtySeven = false,
  unlockedElement = null
}) => {
  if (gameState === 'dead') {
      return <DeathScreen reason={deathReason || "Unknown Cause"} />;
  }

  // Combine dashboard slots and lab-created slots for display (max 12 total: 8 dashboard + 4 lab-created)
  const displayElements = [...labSlots, ...labCreatedSlots];

  // Helper for font scaling
  const getSymbolScaleClass = (symbol: string, context: 'shelf' | 'system' | 'center') => {
      const len = symbol.length;
      
      if (context === 'shelf') {
          if (len > 4) return "text-sm";
          if (len > 2) return "text-lg";
          return "text-2xl";
      }
      
      if (context === 'system') {
          if (len > 4) return "text-4xl";
          if (len > 2) return "text-5xl";
          return "text-6xl";
      }
      
      if (context === 'center') {
           if (len > 4) return "text-5xl md:text-7xl";
           return "text-7xl md:text-9xl";
      }
      
      return "";
  };

  // Animation Loop for UI Updates (No React Render Lag)
  useEffect(() => {
    // Skip all gesture effects when dashboard is open
    if (isDashboardOpen) return;

    let animId: number;
    const cursorLeft = document.getElementById("cursor-left");
    const cursorRight = document.getElementById("cursor-right");

    const updateUI = () => {
      // Early return if dashboard opened during animation
      if (isDashboardOpen) {
        // Hide cursors
        if (cursorLeft) cursorLeft.style.opacity = "0";
        if (cursorRight) cursorRight.style.opacity = "0";
        return;
      }

      const data = trackingRef.current;
      const screenW = window.innerWidth;
      const screenH = window.innerHeight;
      const screenAspect = screenW / screenH;
      const videoAspect = data.cameraAspect;

      // Coordinate Remapping Logic
      const getScreenCoords = (nx: number, ny: number) => {
        let x, y;
        if (screenAspect > videoAspect) {
          const videoH_pixels = (1 / videoAspect) * screenW;
          const offsetY = (videoH_pixels - screenH) / 2;
          x = nx * screenW;
          y = ny * videoH_pixels - offsetY;
        } else {
          const videoW_pixels = videoAspect * screenH;
          const offsetX = (videoW_pixels - screenW) / 2;
          x = nx * videoW_pixels - offsetX;
          y = ny * screenH;
        }
        return { x, y };
      };

      // 0. Update Cursors (Using Index Position)
      if (cursorLeft) {
        if (data.left.isPresent) {
            const l = getScreenCoords(data.left.indexPosition.x, data.left.indexPosition.y);
            cursorLeft.style.transform = `translate(${l.x}px, ${l.y}px)`;
            cursorLeft.style.opacity = '1';
            if (data.left.isPinching) {
                cursorLeft.classList.add('scale-75', 'cursor-pinching-left');
                cursorLeft.classList.remove('scale-100', 'cursor-default-left');
            } else {
                cursorLeft.classList.add('scale-100', 'cursor-default-left');
                cursorLeft.classList.remove('scale-75', 'cursor-pinching-left');
            }
        } else {
          cursorLeft.style.opacity = "0";
        }
      }

      if (cursorRight) {
        if (data.right.isPresent) {
            const r = getScreenCoords(data.right.indexPosition.x, data.right.indexPosition.y);
            cursorRight.style.transform = `translate(${r.x}px, ${r.y}px)`;
            cursorRight.style.opacity = '1';
            if (data.right.isPinching) {
                cursorRight.classList.add('scale-75', 'cursor-pinching-right');
                cursorRight.classList.remove('scale-100', 'cursor-default-right');
            } else {
                cursorRight.classList.add('scale-100', 'cursor-default-right');
                cursorRight.classList.remove('scale-75', 'cursor-pinching-right');
            }
        } else {
          cursorRight.style.opacity = "0";
        }
      }

      // 1. Highlight Items (Shelf + Catalyst + Dashboard) on Hover
      const interactables = document.querySelectorAll(".interactable-btn");
      interactables.forEach((item) => {
        const rect = item.getBoundingClientRect();
        let isHovered = false;

        // Check Left Hand (Index)
        if (data.left.isPresent) {
          const l = getScreenCoords(
            data.left.indexPosition.x,
            data.left.indexPosition.y
          );
          if (
            l.x >= rect.left &&
            l.x <= rect.right &&
            l.y >= rect.top &&
            l.y <= rect.bottom
          )
            isHovered = true;
        }
        // Check Right Hand (Index)
        if (data.right.isPresent) {
          const r = getScreenCoords(
            data.right.indexPosition.x,
            data.right.indexPosition.y
          );
          if (
            r.x >= rect.left &&
            r.x <= rect.right &&
            r.y >= rect.top &&
            r.y <= rect.bottom
          )
            isHovered = true;
        }

        // Apply Hover Styles Direct to DOM
        const el = item as HTMLElement;
        const isShelfItem = el.id.startsWith("shelf-item-");
        const isCatalystItem = el.id.startsWith("catalyst-btn-");
        const isDashboardItem = el.id.startsWith("dashboard-");
        const isToggle = el.id === "dashboard-toggle";

        if (isHovered) {
          el.style.transform = "scale(1.05)";
          el.style.zIndex = "100";

          if (isShelfItem) {
            el.style.borderColor = HOLOLAB_COLORS.precisionBlue;
            el.style.backgroundColor = HOLOLAB_COLORS.labWhite;
            el.style.boxShadow = `0 4px 12px ${HOLOLAB_COLORS.deepBlue}20`;
          } else if (isCatalystItem) {
             const active = el.dataset.active === 'true';
             el.style.borderColor = active ? el.dataset.activecolor! : HOLOLAB_COLORS.precisionBlue;
             el.style.boxShadow = `0 4px 12px ${HOLOLAB_COLORS.deepBlue}20`;
             el.style.backgroundColor = HOLOLAB_COLORS.labWhite;
          } else if (isToggle || isDashboardItem) {
            el.style.boxShadow = `0 4px 12px ${HOLOLAB_COLORS.deepBlue}20`;
          }
        } else {
          // Revert to base styles
          el.style.transform = "scale(1)";
          el.style.zIndex = "1";

          if (isShelfItem) {
            const isLeftActive = el.dataset.symbol === leftElement.symbol;
            const isRightActive = el.dataset.symbol === rightElement.symbol;

            if (isLeftActive) {
              el.style.borderColor = HOLOLAB_COLORS.precisionBlue;
              el.style.boxShadow = `0 2px 8px ${HOLOLAB_COLORS.deepBlue}15`;
            } else if (isRightActive) {
              el.style.borderColor = HOLOLAB_COLORS.indigo;
              el.style.boxShadow = `0 2px 8px ${HOLOLAB_COLORS.deepBlue}15`;
            } else {
              el.style.borderColor = `${HOLOLAB_COLORS.deepBlue}15`;
              el.style.boxShadow = "none";
            }
            el.style.backgroundColor = HOLOLAB_COLORS.labWhite;
          } else if (isCatalystItem) {
            const active = el.dataset.active === "true";
            el.style.backgroundColor = HOLOLAB_COLORS.labWhite;
            el.style.borderColor = active
              ? el.dataset.activecolor!
              : `${HOLOLAB_COLORS.deepBlue}15`;
            el.style.boxShadow = active
              ? `0 2px 8px ${el.dataset.activecolor}40`
              : "none";
            el.style.color = active ? el.dataset.activecolor! : HOLOLAB_COLORS.deepBlue;
          } else if (isToggle) {
            el.style.boxShadow = "none";
          }
        }
      });

      animId = requestAnimationFrame(updateUI);
    };

    animId = requestAnimationFrame(updateUI);
    return () => cancelAnimationFrame(animId);
  }, [
    leftElement,
    rightElement,
    activeCatalyst,
    displayElements,
    isDashboardOpen,
  ]);

  // Render Cursors always, but hide other UI if dashboard is open
  return (
    <div className="absolute inset-0 pointer-events-none z-10 flex flex-col justify-between">
      {/* CURSORS (Index Finger Based) - Hidden when dashboard is open */}
      {!isDashboardOpen && (
        <>
          <div
            id="cursor-left"
            className="fixed top-0 left-0 w-10 h-10 rounded-full border-2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-50 transition-all duration-150 ease-out flex items-center justify-center cursor-default-left"
            style={{ borderColor: HOLOLAB_COLORS.precisionBlue, backgroundColor: `${HOLOLAB_COLORS.precisionBlue}20` }}
          >
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: HOLOLAB_COLORS.precisionBlue }}></div>
          </div>
          <div
            id="cursor-right"
            className="fixed top-0 left-0 w-8 h-8 rounded-full border-2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-[200] transition-colors duration-75 flex items-center justify-center cursor-default-right"
            style={{ borderColor: HOLOLAB_COLORS.indigo, backgroundColor: `${HOLOLAB_COLORS.indigo}20` }}
          >
            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: HOLOLAB_COLORS.indigo }}></div>
          </div>
        </>
      )}

      {/* MAIN UI - Hide when Dashboard Open */}
      <div
        className={`flex-1 flex flex-col justify-between transition-opacity duration-300 ${isDashboardOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`}
      >
        {/* HEADER BAR - REARRANGED */}
        <div className="w-full pt-4 px-6 flex flex-row justify-between items-start pointer-events-auto relative z-20">
          {/* LEFT HAND SYSTEM - Clean Card */}
          <div
            className={`text-left transition-all duration-500 ${combinedElement ? "opacity-0 translate-y-10" : "opacity-100"}`}
          >
            <div
              className="bg-white rounded-xl p-4 shadow-sm"
              style={{ border: `1px solid ${HOLOLAB_COLORS.deepBlue}08` }}
            >
              <div className="text-[10px] font-['Inter'] font-medium tracking-wide mb-2 pb-1 inline-block" style={{ color: HOLOLAB_COLORS.precisionBlue, borderBottom: `1px solid ${HOLOLAB_COLORS.precisionBlue}30` }}>
                LEFT HAND
              </div>
              <div className={`${getSymbolScaleClass(leftElement.symbol, 'system')} font-['Inter'] font-bold`} style={{ color: HOLOLAB_COLORS.deepBlue }}>
                {leftElement.symbol}
              </div>
              <div className="text-sm mt-1 font-['JetBrains_Mono']" style={{ color: HOLOLAB_COLORS.precisionBlue }}>
                {leftElement.name}
              </div>
            </div>
          </div>

          {/* CENTER: LAB SHELF - Clean Cards */}
          <div className="flex-1 overflow-visible px-4 flex justify-center max-w-4xl">
            <div className="w-full overflow-x-visible pb-4 no-scrollbar flex justify-center">
              {displayElements.length > 0 ? (
                <div className="flex gap-3 px-4 min-w-max justify-center items-center">
                  {displayElements.map((el) => {
                    const isLeft = leftElement.symbol === el.symbol;
                    const isRight = rightElement.symbol === el.symbol;

                    return (
                      <div
                        key={el.symbol}
                        id={`shelf-item-${el.symbol}`}
                        data-symbol={el.symbol}
                        data-color={el.color}
                        className="interactable-btn w-16 h-16 rounded-xl flex flex-col items-center justify-center relative transition-all duration-200 cursor-pointer"
                        style={{
                          backgroundColor: HOLOLAB_COLORS.labWhite,
                          border: `1px solid ${isLeft ? HOLOLAB_COLORS.precisionBlue : isRight ? HOLOLAB_COLORS.indigo : HOLOLAB_COLORS.deepBlue + '15'}`,
                          boxShadow: (isLeft || isRight) ? `0 2px 8px ${HOLOLAB_COLORS.deepBlue}15` : 'none'
                        }}
                      >
                        <div
                          className={`${getSymbolScaleClass(el.symbol, 'shelf')} font-bold font-['Inter']`}
                          style={{ color: el.color }}
                        >
                          {el.symbol}
                        </div>
                        <div className="text-[8px] font-['JetBrains_Mono'] mt-0.5" style={{ color: HOLOLAB_COLORS.deepBlue, opacity: 0.6 }}>
                          {el.name.substring(0, 6)}
                        </div>

                        {isLeft && (
                          <div
                            className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-[8px] font-['Inter'] font-semibold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: HOLOLAB_COLORS.precisionBlue, color: HOLOLAB_COLORS.labWhite }}
                          >
                            L
                          </div>
                        )}
                        {isRight && (
                          <div
                            className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-[8px] font-['Inter'] font-semibold px-2 py-0.5 rounded-full"
                            style={{ backgroundColor: HOLOLAB_COLORS.indigo, color: HOLOLAB_COLORS.labWhite }}
                          >
                            R
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  className="flex items-center justify-center px-6 py-4 text-center rounded-xl"
                  style={{ backgroundColor: HOLOLAB_COLORS.labWhite, border: `1px solid ${HOLOLAB_COLORS.deepBlue}08` }}
                >
                  <div className="font-['Inter'] text-sm" style={{ color: HOLOLAB_COLORS.deepBlue, opacity: 0.5 }}>
                    <div className="mb-1">No elements selected</div>
                    <div className="text-xs font-['JetBrains_Mono']">
                      Open Collection to add elements
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT HAND SYSTEM - Clean Card */}
          <div
            className={`text-right transition-all duration-500 ${combinedElement ? "opacity-0 translate-y-10" : "opacity-100"}`}
          >
            <div
              className="bg-white rounded-xl p-4 shadow-sm"
              style={{ border: `1px solid ${HOLOLAB_COLORS.deepBlue}08` }}
            >
              <div className="text-[10px] font-['Inter'] font-medium tracking-wide mb-2 pb-1 inline-block" style={{ color: HOLOLAB_COLORS.indigo, borderBottom: `1px solid ${HOLOLAB_COLORS.indigo}30` }}>
                RIGHT HAND
              </div>
              <div className={`${getSymbolScaleClass(rightElement.symbol, 'system')} font-['Inter'] font-bold`} style={{ color: HOLOLAB_COLORS.deepBlue }}>
                {rightElement.symbol}
              </div>
              <div className="text-sm mt-1 font-['JetBrains_Mono']" style={{ color: HOLOLAB_COLORS.indigo }}>
                {rightElement.name}
              </div>
            </div>
          </div>
        </div>

        {/* --- COLLECTION BUTTON (BOTTOM LEFT) - Clean Card --- */}
        <div className="absolute bottom-10 left-10 pointer-events-auto z-40">
          <div
            id="dashboard-toggle"
            onClick={onToggleDashboard}
            className="interactable-btn flex items-center gap-3 px-5 py-3 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md"
            style={{
              backgroundColor: HOLOLAB_COLORS.labWhite,
              border: `1px solid ${HOLOLAB_COLORS.deepBlue}08`,
              color: HOLOLAB_COLORS.deepBlue
            }}
          >
            <DashboardIcon />
            <span className="font-['Inter'] text-sm font-semibold tracking-wide">
              Collection
            </span>
          </div>
        </div>

        {/* --- BOTTOM CATALYST PANEL - Clean Cards --- */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-auto z-30">
          <div className="flex flex-row gap-4">
            <div
              id="catalyst-btn-heat"
              data-type="heat"
              data-active={activeCatalyst === "heat"}
              data-activecolor={HOLOLAB_COLORS.warningAmber}
              className="interactable-btn w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                backgroundColor: HOLOLAB_COLORS.labWhite,
                border: `1px solid ${activeCatalyst === 'heat' ? HOLOLAB_COLORS.warningAmber : HOLOLAB_COLORS.deepBlue + '15'}`,
                color: activeCatalyst === 'heat' ? HOLOLAB_COLORS.warningAmber : HOLOLAB_COLORS.deepBlue,
                boxShadow: activeCatalyst === 'heat' ? `0 2px 8px ${HOLOLAB_COLORS.warningAmber}40` : 'none'
              }}
            >
              <div className="scale-75">
                <FlameIcon />
              </div>
            </div>

            <div
              id="catalyst-btn-light"
              data-type="light"
              data-active={activeCatalyst === "light"}
              data-activecolor={HOLOLAB_COLORS.infoBlue}
              className="interactable-btn w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                backgroundColor: HOLOLAB_COLORS.labWhite,
                border: `1px solid ${activeCatalyst === 'light' ? HOLOLAB_COLORS.infoBlue : HOLOLAB_COLORS.deepBlue + '15'}`,
                color: activeCatalyst === 'light' ? HOLOLAB_COLORS.infoBlue : HOLOLAB_COLORS.deepBlue,
                boxShadow: activeCatalyst === 'light' ? `0 2px 8px ${HOLOLAB_COLORS.infoBlue}40` : 'none'
              }}
            >
              <div className="scale-75">
                <BoltIcon />
              </div>
            </div>

            <div
              id="catalyst-btn-chemical"
              data-type="chemical"
              data-active={activeCatalyst === "chemical"}
              data-activecolor={HOLOLAB_COLORS.successGreen}
              className="interactable-btn w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                backgroundColor: HOLOLAB_COLORS.labWhite,
                border: `1px solid ${activeCatalyst === 'chemical' ? HOLOLAB_COLORS.successGreen : HOLOLAB_COLORS.deepBlue + '15'}`,
                color: activeCatalyst === 'chemical' ? HOLOLAB_COLORS.successGreen : HOLOLAB_COLORS.deepBlue,
                boxShadow: activeCatalyst === 'chemical' ? `0 2px 8px ${HOLOLAB_COLORS.successGreen}40` : 'none'
              }}
            >
              <div className="scale-75">
                <FlaskIcon />
              </div>
            </div>

            <div
              id="catalyst-btn-pressure"
              data-type="pressure"
              data-active={activeCatalyst === "pressure"}
              data-activecolor="#9333EA"
              className="interactable-btn w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-200"
              style={{
                backgroundColor: HOLOLAB_COLORS.labWhite,
                border: `1px solid ${activeCatalyst === 'pressure' ? '#9333EA' : HOLOLAB_COLORS.deepBlue + '15'}`,
                color: activeCatalyst === 'pressure' ? '#9333EA' : HOLOLAB_COLORS.deepBlue,
                boxShadow: activeCatalyst === 'pressure' ? '0 2px 8px rgba(147, 51, 234, 0.4)' : 'none'
              }}
            >
              <div className="scale-75">
                <PressureIcon />
              </div>
            </div>
          </div>
          <div className="text-[9px] font-['Inter'] tracking-wide" style={{ color: HOLOLAB_COLORS.labWhite, opacity: 0.5 }}>
            Hover to select · Pinch to deactivate
          </div>
        </div>

        {/* --- BOTTOM HUD --- */}
        <div className="p-6 md:p-10 flex flex-col justify-end pointer-events-none">
          {/* Center Message - Clean Style */}
          {combinedElement && combinedElement.symbol !== 'BOOM' && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center w-full pointer-events-none">
              <div
                className="inline-block bg-white rounded-2xl px-12 py-8 shadow-lg"
                style={{ border: `1px solid ${HOLOLAB_COLORS.deepBlue}08` }}
              >
                <h2 className={`${getSymbolScaleClass(combinedElement.symbol, 'center')} font-['Inter'] font-bold`} style={{ color: HOLOLAB_COLORS.deepBlue }}>
                  {combinedElement.symbol}
                </h2>
                <div className="mt-4 text-xl font-['JetBrains_Mono'] tracking-wide uppercase" style={{ color: HOLOLAB_COLORS.precisionBlue }}>
                  {combinedElement.name}
                </div>
                <div className="mt-4 text-xs font-['Inter']" style={{ color: HOLOLAB_COLORS.successGreen }}>
                  Close fist to save element
                </div>
              </div>
            </div>
          )}

          {/* Status Ticker - Clean Minimal Style */}
          <div className="absolute bottom-44 left-1/2 transform -translate-x-1/2 text-center w-full pointer-events-none">
            <div
              className="inline-block rounded-xl px-8 py-4 shadow-sm"
              style={{
                backgroundColor: HOLOLAB_COLORS.labWhite,
                border: `1px solid ${HOLOLAB_COLORS.deepBlue}08`
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: message.includes("HOLD")
                      ? HOLOLAB_COLORS.warningAmber
                      : message.includes("SUCCESS") || message.includes("SAVED")
                        ? HOLOLAB_COLORS.successGreen
                        : message.includes("Unstable") || message.includes("Failed") || message.includes("WARNING")
                          ? HOLOLAB_COLORS.errorRed
                          : HOLOLAB_COLORS.precisionBlue
                  }}
                />
                <span
                  className="font-['JetBrains_Mono'] text-sm font-medium tracking-wide"
                  style={{
                    color: message.includes("HOLD")
                      ? HOLOLAB_COLORS.warningAmber
                      : message.includes("SUCCESS") || message.includes("SAVED")
                        ? HOLOLAB_COLORS.successGreen
                        : message.includes("Unstable") || message.includes("Failed") || message.includes("WARNING")
                          ? HOLOLAB_COLORS.errorRed
                          : HOLOLAB_COLORS.deepBlue
                  }}
                >
                  {message}
                </span>
              </div>
            </div>

            {!combinedElement && (
              <div className="mt-4 flex gap-6 justify-center text-[10px] font-['Inter']" style={{ color: HOLOLAB_COLORS.labWhite, opacity: 0.6 }}>
                <span className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: HOLOLAB_COLORS.precisionBlue }}></div>
                  Hover to select
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: HOLOLAB_COLORS.labWhite }}></div>
                  Clap to fuse
                </span>
                <span className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: HOLOLAB_COLORS.errorRed }}></div>
                  Spin to reset
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 67 Gesture Display - Clean Style */}
      {showSixtySeven && (
        <div className="fixed inset-0 flex items-center justify-center z-[200] pointer-events-none">
          <div
            className="text-[20rem] font-['Inter'] font-bold"
            style={{
              color: HOLOLAB_COLORS.deepBlue,
              animation: 'pulse 1s ease-in-out infinite, fadeInOut 3s ease-in-out forwards'
            }}
          >
            67
          </div>
        </div>
      )}

      {/* Achievement Unlocked Popout */}
      {unlockedElement && (
        <div className="fixed top-8 right-8 z-[150] pointer-events-none animate-slideInRight animate-stayLonger">
          <div
            className="rounded-2xl p-6 shadow-2xl border-2 flex items-center gap-4 min-w-[320px]"
            style={{
              backgroundColor: HOLOLAB_COLORS.labWhite,
              borderColor: HOLOLAB_COLORS.successGreen,
              boxShadow: `0 8px 32px ${HOLOLAB_COLORS.successGreen}40`
            }}
          >
            {/* Icon */}
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: HOLOLAB_COLORS.successGreen + '20',
                border: `2px solid ${HOLOLAB_COLORS.successGreen}`
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-8 h-8"
                stroke={HOLOLAB_COLORS.successGreen}
                strokeWidth="2"
              >
                <path
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div
                className="text-xs font-bold tracking-widest uppercase mb-1"
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: HOLOLAB_COLORS.successGreen
                }}
              >
                ELEMENT UNLOCKED
              </div>
              <div
                className="text-2xl font-bold mb-1"
                style={{
                  fontFamily: "'Inter', sans-serif",
                  color: unlockedElement.color
                }}
              >
                {unlockedElement.symbol}
              </div>
              <div
                className="text-sm"
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  color: HOLOLAB_COLORS.deepBlue
                }}
              >
                {unlockedElement.name}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes fadeInOut {
            0% { opacity: 0; transform: scale(0.5); }
            20% { opacity: 1; transform: scale(1); }
            80% { opacity: 1; transform: scale(1); }
            100% { opacity: 0; transform: scale(0.5); }
        }
        @keyframes slideInRight {
            0% {
              opacity: 0;
              transform: translateX(100px);
            }
            100% {
              opacity: 1;
              transform: translateX(0);
            }
        }
        @keyframes stayLonger {
            0%, 90% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
        }
        .animate-slideInRight {
          animation: slideInRight 0.4s ease-out forwards;
        }
        .animate-stayLonger {
          animation: stayLonger 8s ease-in-out forwards;
        }
        .cursor-default-left {
          border-color: ${HOLOLAB_COLORS.precisionBlue};
          background-color: ${HOLOLAB_COLORS.precisionBlue}20;
        }
        .cursor-pinching-left {
          border-color: ${HOLOLAB_COLORS.labWhite};
          background-color: ${HOLOLAB_COLORS.precisionBlue}50;
        }
        .cursor-default-right {
          border-color: ${HOLOLAB_COLORS.indigo};
          background-color: ${HOLOLAB_COLORS.indigo}20;
        }
        .cursor-pinching-right {
          border-color: ${HOLOLAB_COLORS.labWhite};
          background-color: ${HOLOLAB_COLORS.indigo}50;
        }
      `}</style>
    </div>
  );
};

export default UIOverlay;
